import { formatUsdc, formatTokens, formatLeverage } from './decimal.js';
import { normalizePairName } from './pair-names.js';
import type {
  RawPair,
  RawTrade,
  RawOrder,
  RawLimit,
} from './raw.js';
import type {
  Pair,
  PairPosition,
  Fill,
  Order,
  OpenOrder,
  PriceData,
} from '../types.js';
import type { TradePnL } from './calculations.js';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MIN_NOTIONAL = '5.0';
const MIN_NOTIONAL_NUM = 5;
/** Arbitrum L2 block production rate. */
const BLOCKS_PER_SECOND_ARBITRUM = 4;
/** Default period for rollover rate display in `PairCtx`. */
const ROLLOVER_RATE_HOURS = 8;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function lowerHex(value: string | null | undefined): string | null {
  if (!value) return null;
  const lower = value.toLowerCase();
  return lower.startsWith('0x') ? lower : `0x${lower}`;
}

export function pairMaxLeverage(pair: RawPair): number {
  const maxLevString =
    (!pair.maxLeverage || pair.maxLeverage === '0')
      ? (pair.group.maxLeverage ?? '0')
      : pair.maxLeverage;
  return formatLeverage(maxLevString);
}

/**
 * Max collateral that can be removed from a single position before crossing
 * its effective max leverage: `collateral × (1 − currentLeverage / maxLeverage)`.
 */
export function maxWithdrawForPosition(
  collateral: number,
  currentLeverage: number,
  maxLeverage: number,
): number {
  if (collateral <= 0) return 0;
  if (currentLeverage <= 0) return 0;
  if (maxLeverage <= 0) return 0;
  if (currentLeverage >= maxLeverage) return 0;

  const max = collateral * (1 - currentLeverage / maxLeverage);
  if (max < 0) return 0;
  if (max > collateral) return collateral;
  return max;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pair
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build a `Pair` from a raw subgraph pair, merging in live price/market context.
 * Falls back to `lastTradePrice` when no live price is given.
 */
export function formatPair(
  raw: RawPair,
  price?: PriceData,
  builderFeeBps = 0,
): Pair {
  const lastPrice = formatTokens(raw.lastTradePrice);
  const mid = price?.mid;
  const refPrice = mid && mid > 0 ? mid : lastPrice;

  const buyOI_usd = formatTokens(raw.longOI) * lastPrice;
  const sellOI_usd = formatTokens(raw.shortOI) * lastPrice;
  const maxOI_usd = formatUsdc(raw.maxOI);

  const minSz = refPrice > 0 ? MIN_NOTIONAL_NUM / refPrice : 0;
  const maxBSz = refPrice > 0 ? Math.max(0, (maxOI_usd - buyOI_usd) / refPrice) : 0;
  const maxSSz = refPrice > 0 ? Math.max(0, (maxOI_usd - sellOI_usd) / refPrice) : 0;

  const rolloverRate = getRolloverRateDisplay(raw);
  const takerFeeBps = Number(raw.takerFeeP) / 1e4;

  return {
    pairId: String(raw.id),
    pairTo: normalizePairName(raw.to),
    pairFrom: normalizePairName(raw.from),
    minSz: minSz.toString(),
    maxBSz: maxBSz.toString(),
    maxSSz: maxSSz.toString(),
    minNtl: MIN_NOTIONAL,
    maxLeverage: pairMaxLeverage(raw),
    overnightMaxLeverage: raw.overnightMaxLeverage ? formatLeverage(raw.overnightMaxLeverage) : 0,
    rolloverFeePerBlock: formatTokens(raw.lastRolloverLongPure ?? '0').toString(),
    openInterest: (buyOI_usd + sellOI_usd).toString(),
    buyOpenInterest: buyOI_usd.toString(),
    sellOpenInterest: sellOI_usd.toString(),
    maxOpenInterest: maxOI_usd.toString(),
    category: raw.group.name,
    rolloverRate,
    midPx: price ? price.mid.toString() : '0',
    askPx: price ? price.ask.toString() : '0',
    bidPx: price ? price.bid.toString() : '0',
    isMarketOpen: price?.isMarketOpen ?? true,
    isDayTradingClosed: price?.isDayTradingClosed ?? false,
    secondsToToggleIsDayTradingClosed: price?.secondsToToggleIsDayTradingClosed ?? -1,
    schedule: price?.schedule,
    openFee: takerFeeBps + builderFeeBps,
    closeFee: 0,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Rollover rate (8hr percent, long & short)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * UI-display rollover rate using Ostium's contract sign convention:
 * positive contract fee = trader pays => display as negative.
 */
function getRolloverRateDisplay(raw: RawPair): { long: string; short: string } {
  const longPure = formatTokens(raw.lastRolloverLongPure ?? '0');
  const premium = formatTokens(raw.brokerPremium ?? '0');

  let contractLong = longPure + premium;
  let contractShort = -longPure + premium;

  if (!raw.isNegativeRolloverAllowed) {
    contractLong = Math.max(contractLong, 0);
    contractShort = Math.max(contractShort, 0);
  }

  const displayPerBlockLong = -contractLong;
  const displayPerBlockShort = -contractShort;

  const blocksInPeriod = ROLLOVER_RATE_HOURS * 60 * 60 * BLOCKS_PER_SECOND_ARBITRUM;
  const periodMultiplier = blocksInPeriod * 100;

  return {
    long: (displayPerBlockLong * periodMultiplier).toString(),
    short: (displayPerBlockShort * periodMultiplier).toString(),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Open positions
// ─────────────────────────────────────────────────────────────────────────────

export function formatPosition(
  raw: RawTrade,
  price: PriceData | undefined,
  pnl: TradePnL,
  maxLeverage: number
): PairPosition {
  const collateral = formatUsdc(raw.collateral);
  const tradeNotional = formatTokens(raw.tradeNotional);
  const leverage = formatLeverage(raw.leverage);
  const mid = price?.mid ?? formatTokens(raw.pair.lastTradePrice);
  const ntl = tradeNotional * mid;
  const roe = collateral > 0 ? pnl.netPnl / collateral : 0;

  return {
    position: {
      pairTo: normalizePairName(raw.pair.to),
      pairFrom: normalizePairName(raw.pair.from),
      pairId: String(raw.pair.id),
      pid: raw.tradeID,
      trader: lowerHex(raw.trader) ?? '',
      idx: parseInt(raw.index || '0') || 0,
      side: raw.isBuy ? 'B' : 'S',
      szi: tradeNotional.toString(),
      entryPx: formatTokens(raw.openPrice).toString(),
      leverage: leverage.toString(),
      ntl: ntl.toString(),
      unrealizedPnl: pnl.netPnl.toString(),
      returnOnEquity: roe.toString(),
      liquidationPx: pnl.liquidationPrice.toString(),
      collateralUsed: collateral.toString(),
      cumRollover: (pnl.rollover * -1).toString(),
      ...(raw.takeProfitPrice && raw.takeProfitPrice !== '0' ? { tpPx: formatTokens(raw.takeProfitPrice).toString() } : {}),
      ...(raw.stopLossPrice && raw.stopLossPrice !== '0' ? { slPx: formatTokens(raw.stopLossPrice).toString() } : {}),
      openTimestamp: parseInt(raw.timestamp || '0') * 1000,
      isDayTrade: raw.isDayTrade ?? false,
      maxLeverage: maxLeverage.toString(),
      maxWithdrawable: maxWithdrawForPosition(collateral, leverage, maxLeverage).toString(),
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Fills
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Order ids are the Trading contract's uint256 counter. The subgraph and the
 * fast overlay both carry it as a base-10 string today; canonicalize so `oid`
 * stays one comparable id space even if a data source hex-encodes it.
 */
function normalizeOrderId(id: string): string {
  try {
    return BigInt(id).toString();
  } catch {
    return id;
  }
}

export function formatFill(raw: RawOrder): Fill {
  const collateral = formatUsdc(raw.collateral);
  const notional = formatUsdc(raw.notional);
  const tradeNotional = formatTokens(raw.tradeNotional);
  const priceImpactP = formatTokens(raw.priceImpactP);
  const devFee = formatUsdc(raw.devFee);
  const vaultFee = formatUsdc(raw.vaultFee);
  const oracleFee = formatUsdc(raw.oracleFee);
  const rolloverFee = formatUsdc(raw.rolloverFee);
  const liquidation = formatUsdc(raw.liquidationFee);
  const builderFee = formatUsdc(raw.builderFee);
  const totalProfitPercent = formatUsdc(raw.totalProfitPercent);

  // Pass the subgraph's orderAction / orderType through verbatim so non-trade
  // actions like `RemoveCollateral` / `TopUpCollateral` aren't misreported as `Open`.
  const action = raw.orderAction;
  const type = raw.orderType;

  const isClose = action === 'Close' || action === 'StopLoss' || action === 'TakeProfit' || action === 'CloseDayTrade';

  const opening = action === 'Open' ? (devFee + vaultFee).toString() : '0';
  const rollover = isClose ? rolloverFee.toString() : '0';
  const liquidationFee = action === 'Liquidation' ? liquidation.toString() : '0';
  const closedPnl = isClose || action === 'Liquidation'
    ? (collateral * totalProfitPercent / 100 - devFee - vaultFee - oracleFee).toString()
    : '0';
  const px = formatTokens(raw.priceAfterImpact)
  const szi = tradeNotional
  const pxBeforeImpact = raw.isBuy
    ? px / (1 + priceImpactP / 100)
    : px / (1 - priceImpactP / 100);
  const priceImpactFee = Math.abs((px - pxBeforeImpact) * szi);
  const executedAtSec = parseInt(raw.executedAt || '0', 10);

  return {
    pairTo: normalizePairName(raw.pair.to),
    pairFrom: normalizePairName(raw.pair.from),
    pairId: String(raw.pair.id),
    oid: normalizeOrderId(raw.id),
    pid: raw.tradeID,
    trader: lowerHex(raw.trader) ?? '',
    side: raw.isBuy ? 'B' : 'S',
    action,
    type,
    px: px.toString(),
    szi: szi.toString(),
    ntl: notional.toString(),
    collateralUsed: collateral.toString(),
    builder: lowerHex(raw.builder) ?? '',
    fees: {
      opening,
      rollover,
      liquidation: liquidationFee,
      builder: builderFee.toString(),
      priceImpact: priceImpactFee.toString(),
    },
    closedPnl,
    hash: lowerHex(raw.executedTx) ?? '',
    time: executedAtSec,
    timestamp: executedAtSec,
  };
}

export function formatOrder(raw: RawOrder): Order {
  return {
    ...formatFill(raw),
    initiatedTx: lowerHex(raw.initiatedTx) ?? '',
    initiatedTime: parseInt(raw.initiatedAt || '0') * 1000,
    isPending: raw.isPending,
    isCancelled: raw.isCancelled,
    ...(raw.initiatedBlock ? { initiatedBlock: raw.initiatedBlock } : {}),
    ...(raw.cancelReason ? { cancelReason: raw.cancelReason } : {}),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Open limit orders
// ─────────────────────────────────────────────────────────────────────────────

export function formatOpenOrder(raw: RawLimit): OpenOrder {
  const tp = raw.takeProfitPrice && raw.takeProfitPrice !== '0' ? formatTokens(raw.takeProfitPrice) : null;
  const sl = raw.stopLossPrice && raw.stopLossPrice !== '0' ? formatTokens(raw.stopLossPrice) : null;

  const orderType = raw.limitType === 'STOP' ? 'Stop' : 'Limit';
  const collateral = formatUsdc(raw.collateral);
  const leverage = formatLeverage(raw.leverage);
  const rawNotional = formatUsdc(raw.notional);
  const notional = rawNotional > 0 ? rawNotional : collateral * leverage;
  const limitPx = formatTokens(raw.openPrice);
  const tradeNotional = formatTokens(raw.tradeNotional);

  // check if pair.from is USD
  const isUsd = raw.pair.from === "USD";
  let size = 0.0;
  if (isUsd) {
    size = tradeNotional > 0
      ? tradeNotional
      : (limitPx > 0 ? notional * limitPx : 0);
  } else {
    size = tradeNotional > 0
      ? tradeNotional
      : (limitPx > 0 ? notional / limitPx : 0);
  }

  return {
    pairTo: normalizePairName(raw.pair.to),
    pairFrom: normalizePairName(raw.pair.from),
    pairId: String(raw.pair.id),
    trader: lowerHex(raw.trader) ?? '',
    // On-chain limit slot index — the trailing segment of the subgraph id
    // (`<trader>_<pairIndex>_<index>`). `orderId` is the global order counter,
    // which overflows the contract's uint8 `index` arg for cancel/modify.
    idx: parseInt(raw.id.split('_').pop() || '0', 10) || 0,
    side: raw.isBuy ? 'B' : 'S',
    limitPx: limitPx.toString(),
    szi: size.toString(),
    ntl: notional.toString(),
    collateralUsed: collateral.toString(),
    leverage: leverage.toString(),
    ...(tp !== null ? { tpPx: tp.toString() } : {}),
    ...(sl !== null ? { slPx: sl.toString() } : {}),
    orderType,
    timestamp: parseInt(raw.initiatedAt || '0') * 1000,
  };
}
